"use client";

import { useState } from "react";
import {
  Calendar,
  Clock,
  Code,
  Database,
  Globe,
  GraduationCap,
  Server,
  User,
  Cpu,
  Network,
} from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "../Header/Footer";
import Navbar from "../Header/Navbar";
import ApplyButton from "../common/ApplyButton";

const MscsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllSemesters, setShowAllSemesters] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="font-sans">
      {/* Header */}
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-800 to-green-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Master of Science in Computer Science
              </h1>
              <p className="text-xl mb-6 text-green-100">
                Advance your expertise in computer science and prepare for
                leadership roles
              </p>
              {/* Apply Button */}
              <div className="flex flex-col md:flex-row gap-4 mt-8">
                <ApplyButton
                  to="/login"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Apply Now
                </ApplyButton>
              </div>
            </div>
            <div
              className="md:w-1/2"
              style={{ height: "30rem", width: "43rem" }}
            >
              <img
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="MSCS Students"
                className="rounded-lg shadow-2xl transform hover:scale-105 transition duration-300"
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Calendar className="text-green-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Program Duration
              </h3>
              <p className="text-gray-600">2 Years (4 Semesters)</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <GraduationCap className="text-green-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Degree Awarded
              </h3>
              <p className="text-gray-600">MS in Computer Science</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Clock className="text-green-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Credit Hours
              </h3>
              <p className="text-gray-600">30+ Credit Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Program Details
            </h2>
            <p className="text-center text-gray-600 max-w-5xl mx-auto text-lg">
              The Master of Science in Computer Science (MSCS) at NIIT, Lahore,
              is a 2-year HEC-recognized postgraduate program comprising 30
              credit hours spread across 4 semesters. Designed for graduates
              with a four-year BS degree or equivalent in the relevant field,
              the program offers specialization in various streams including
              Software Development, Cloud Computing, Design and Analysis of
              Algorithms, Computer Systems and Network, and Cybersecurity.
              Students can pursue either a thesis or non-thesis track, with the
              program preparing them for leadership roles in academia, research,
              and industry.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
            {["overview", "curriculum", "admission", "careers"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-6 font-medium text-sm focus:outline-none transition duration-300 ${
                  activeTab === tab
                    ? "border-b-2 border-green-600 text-green-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            {activeTab === "overview" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Program Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  The MSCS program provides a rigorous academic environment that
                  bridges theory with practice, offering advanced training in
                  core computing concepts as well as cutting-edge
                  specializations. Students engage in analytical
                  problem-solving, software architecture, machine learning,
                  distributed systems, and cybersecurity, among other topics.
                  With strong academic mentoring and access to research labs,
                  students are empowered to contribute to scientific
                  advancements and industry innovation.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: <Cpu className="text-green-600" size={36} />,
                      title: "Specialized Learning Tracks",
                      description:
                        "Delve into focused areas of study such as artificial intelligence, data analytics, software systems, and cloud infrastructure. Choose from a range of electives to tailor your learning according to career goals and research interests.",
                    },
                    {
                      icon: <User className="text-green-600" size={36} />,
                      title: "Distinguished Mentorship",
                      description:
                        "Be guided by faculty with exceptional academic credentials and professional experience. Their expertise bridges theoretical foundations with real-world insights, enriching the graduate learning experience.",
                    },
                    {
                      icon: <Server className="text-green-600" size={36} />,
                      title: "Innovative Research Pathways",
                      description:
                        "Engage in groundbreaking research across high-impact domains like machine learning, cybersecurity, and intelligent automation. Students are encouraged to contribute to academic publications and conferences.",
                    },
                    {
                      icon: <Globe className="text-green-600" size={36} />,
                      title: "Professional Integration",
                      description:
                        "Take advantage of strategic partnerships with leading tech companies. Internship placements, industry-led projects, and networking events help translate classroom learning into professional advancement.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-gray-50 p-6 rounded-lg transform hover:scale-105 transition duration-300"
                    >
                      <div className="bg-white p-3 rounded-full mr-4 shadow-md">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "curriculum" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Curriculum
                </h3>
                <p className="text-gray-700 mb-6">
                  Our comprehensive curriculum is designed to provide advanced
                  knowledge in computer science while offering specialization
                  opportunities in emerging technologies.
                </p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Streams Available
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {[
                      "Software Development",
                      "Cloud Computing",
                      "Design and Analysis of Algorithms",
                      "Computer Systems and Network",
                      "Cybersecurity",
                    ].map((stream, index) => (
                      <div
                        key={index}
                        className="bg-green-50 p-3 rounded-lg border border-green-200 text-center"
                      >
                        <p className="font-medium text-gray-800">{stream}</p>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Scheme of Studies
                  </h4>

                  {/* Semester 1 */}
                  <div className="mb-8">
                    <h5 className="text-lg font-semibold text-green-700 mb-3">
                      Semester I
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                              Course
                            </th>
                            <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                              Credits
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Analysis of Algorithms
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Computer Architecture
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              CS Elective Course - I
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="bg-green-50 font-medium">
                            <td className="py-2 px-4 border-b text-sm">
                              Total Credit Hours
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              9
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Semester 2 */}
                  <div className="mb-8">
                    <h5 className="text-lg font-semibold text-green-700 mb-3">
                      Semester II
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                              Course
                            </th>
                            <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                              Credits
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Theory of Computation
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              Advanced Operating Systems
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              CS Elective Course - II
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="hover:bg-gray-50">
                            <td className="py-2 px-4 border-b text-sm">
                              CS Elective Course - III
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              3
                            </td>
                          </tr>
                          <tr className="bg-green-50 font-medium">
                            <td className="py-2 px-4 border-b text-sm">
                              Total Credit Hours
                            </td>
                            <td className="py-2 px-4 border-b text-sm text-center">
                              12
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Show More Button */}
                  {!showAllSemesters && (
                    <div className="flex justify-center mt-8 mb-8">
                      <button
                        onClick={() => setShowAllSemesters(true)}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                      >
                        Show More Semesters
                      </button>
                    </div>
                  )}

                  {/* Semesters 3-4 (conditionally rendered) */}
                  {showAllSemesters && (
                    <>
                      {/* Semester 3 */}
                      <div className="mb-8">
                        <h5 className="text-lg font-semibold text-green-700 mb-3">
                          Semester III
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Credits
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  CS Elective Course - IV
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  3
                                </td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  MS Thesis
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  6
                                </td>
                              </tr>
                              <tr className="bg-green-50 font-medium">
                                <td className="py-2 px-4 border-b text-sm">
                                  Total Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  9
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Semester 4 */}
                      <div className="mb-8">
                        <h5 className="text-lg font-semibold text-green-700 mb-3">
                          Semester IV
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Credits
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b text-sm">
                                  MS Thesis (continue)
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  -
                                </td>
                              </tr>
                              <tr className="bg-green-50 font-medium">
                                <td className="py-2 px-4 border-b text-sm">
                                  Total Degree Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  30
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-8 bg-green-50 p-6 rounded-lg shadow-md">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Program Summary
                  </h4>
                  <p className="text-gray-700 mb-4">
                    The MS Computer Science program requires a total of 30
                    credit hours to graduate, with a mix of core courses,
                    electives, and a thesis component.
                  </p>
                </div>
              </div>
            )}

            {activeTab === "admission" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Admission Requirements
                </h3>
                <p className="text-gray-700 mb-6">
                  To be eligible for admission to the MSCS program, applicants
                  must meet the following requirements:
                </p>

                <div className="space-y-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Academic Requirements
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Four-year HEC-recognized BS degree/equivalent degree in
                        the relevant field with at least 120 CH beyond 12 years
                        of schooling
                      </li>
                      <li>
                        CGPA of at least 2.00 (on a scale of 4.00) OR First
                        Division under the annual system
                      </li>
                      <li>
                        In the case of a foreign degree, HEC equivalence is
                        mandatory
                      </li>
                      <li>
                        Strong background in programming and computer science
                        fundamentals
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Entrance Test
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Qualifying score (50% or above) in the University MS
                        Admission Test
                      </li>
                      <li>
                        OR evidence of having passed the GRE/Equivalent test
                        with a minimum of 50% marks
                      </li>
                      <li>
                        Test covers Computer Science fundamentals, Programming,
                        and Mathematics
                      </li>
                    </ul>
                  </div>

                  {/* <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">Required Documents</h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Completed application form</li>
                      <li>Official transcripts from all post-secondary institutions attended</li>
                      <li>Statement of purpose</li>
                      <li>Two letters of recommendation</li>
                      <li>Updated curriculum vitae/resume</li>
                      <li>Copy of CNIC</li>
                      <li>Four recent passport-sized photographs</li>
                    </ul>
                  </div> */}

                  <div className="bg-green-50 p-6 rounded-lg shadow-md border-l-4 border-green-500">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Application Process
                    </h4>
                    <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                      <li>Submit online application through the NIIT portal</li>
                      <li>Pay application fee</li>
                      <li>Appear for entrance test on the scheduled date</li>
                      <li>
                        Shortlisted candidates will be called for interview
                      </li>
                      <li>
                        Final selection based on academic record, entrance test,
                        and interview performance
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "careers" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Career Prospects
                </h3>
                <p className="text-gray-700 mb-6">
                  Graduates of our MSCS program are well-prepared for advanced
                  career opportunities in the technology sector. The program
                  equips students with the expertise needed to excel in various
                  roles.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Job Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Code size={20} />,
                          title: "Senior Software Engineer",
                          description:
                            "Lead development teams and architect complex software systems",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Database Architect",
                          description:
                            "Design and optimize large-scale database systems",
                        },
                        {
                          icon: <Network size={20} />,
                          title: "Network Security Specialist",
                          description:
                            "Protect organizational networks and systems from threats",
                        },
                        {
                          icon: <Server size={20} />,
                          title: "Cloud Solutions Architect",
                          description:
                            "Design and implement cloud-based infrastructure and applications",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Advanced Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Cpu size={20} />,
                          title: "Research Scientist",
                          description:
                            "Conduct research in specialized areas of computer science",
                        },
                        {
                          icon: <Database size={20} />,
                          title: "Data Science Lead",
                          description:
                            "Lead teams in extracting insights from complex data",
                        },
                        {
                          icon: <Globe size={20} />,
                          title: "IT Director",
                          description:
                            "Oversee technology strategy and implementation",
                        },
                        {
                          icon: <Server size={20} />,
                          title: "Technology Consultant",
                          description:
                            "Advise organizations on technology solutions and strategy",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg shadow-md mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Industry Sectors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Technology Companies",
                      "Financial Services",
                      "Healthcare",
                      "E-commerce",
                      "Telecommunications",
                      "Education",
                      "Government",
                      "Research Institutions",
                    ].map((sector, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded text-center shadow-sm"
                      >
                        <p className="font-medium text-gray-700">{sector}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Further Education
                  </h4>
                  <p className="text-gray-700 mb-4">
                    MSCS graduates can pursue advanced degrees such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>PhD in Computer Science</li>
                    <li>PhD in Information Technology</li>
                    <li>PhD in Software Engineering</li>
                    <li>PhD in Cybersecurity</li>
                    <li>PhD in Data Science</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-green-800 to-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Advance Your Career in Computer Science?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our MSCS program and develop the expertise needed to lead in
            the technology industry.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ApplyButton
              to="/login"
              className="bg-white text-green-700 hover:bg-green-50 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
            >
              Apply Now
            </ApplyButton>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MscsPage;
